import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ClerkProvider, useUser } from '@clerk/clerk-react'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'
import { SciChartSurface } from 'scichart'
import App from './App.tsx'
import { ErrorBoundary } from './components/ErrorBoundary'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider, useThemeMode } from './contexts/ThemeContext'
import { type WaveThemeMode } from './styles/theme'
import createWaveTheme from './styles/theme'
import './index.css'

// Import your publishable key
const PUBLISHABLE_KEY = import.meta.env.VITE_WAVE_CLERK_PUBLISHABLE_KEY

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key")
}

// Configure SciChart WASM modules to use locally built files
SciChartSurface.configure({
  wasmUrl: '/scichart/scichart2d.wasm',
  dataUrl: '/scichart/scichart2d.data'
})

// Set SciChart runtime license key from environment variable
const SCICHART_LICENSE_KEY = import.meta.env.VITE_WAVE_SCICHART_LICENSE_KEY
console.log('SCICHART_LICENSE_KEY', SCICHART_LICENSE_KEY)
console.log('VITE_WAVE_SCICHART_LICENSE_KEY', import.meta.env.VITE_WAVE_SCICHART_LICENSE_KEY)
console.log('SCICHART HI')
if (SCICHART_LICENSE_KEY) {
  SciChartSurface.setRuntimeLicenseKey(SCICHART_LICENSE_KEY)
  console.log('✅ SciChart license key configured')
} else {
  console.warn('⚠️ SciChart license key not found - running in trial mode')
}

console.log('✅ SciChart configured with WASM modules')
console.log('✅ React app starting with scichart-react package')

/**
 * Material UI Theme Provider that consumes the theme context
 */
function MaterialUIThemeProvider({ children }: { children: React.ReactNode }) {
  const mode = useThemeMode();
  const theme = createWaveTheme(mode);
  
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}

/**
 * Theme Provider that integrates with Clerk for persistence
 */
function ThemeProviderWithClerk({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  
  // Get initial theme from Clerk metadata
  const waveMetadata = (user?.unsafeMetadata as any)?.wave;
  const initialTheme = waveMetadata?.theme as WaveThemeMode | undefined;
  
  // Debounce theme saves to prevent rate limiting
  const handleThemeChange = React.useCallback(
    React.useMemo(() => {
      let timeoutId: NodeJS.Timeout;
      
      return (theme: WaveThemeMode) => {
        // Clear previous timeout
        clearTimeout(timeoutId);
        
        // Debounce the save for 500ms
        timeoutId = setTimeout(async () => {
          if (user) {
            try {
              await user.update({
                unsafeMetadata: {
                  ...user.unsafeMetadata,
                  wave: {
                    ...((user.unsafeMetadata as any)?.wave || {}),
                    theme
                  }
                }
              });
            } catch (error) {
              console.warn('Failed to save theme to Clerk:', error);
            }
          }
        }, 500);
      };
    }, [user?.id]) // Only recreate when user ID changes, not the entire user object
    , [user?.id]
  );
  
  return (
    <ThemeProvider 
      initialTheme={initialTheme}
      onThemeChange={handleThemeChange}
    >
      {children}
    </ThemeProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <ErrorBoundary>
    <ClerkProvider publishableKey={PUBLISHABLE_KEY}>
      <AuthProvider>
        <ThemeProviderWithClerk>
          <MaterialUIThemeProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </MaterialUIThemeProvider>
        </ThemeProviderWithClerk>
      </AuthProvider>
    </ClerkProvider>
  </ErrorBoundary>
)
